import React, { useState } from 'react'
import { ChallengeService } from '@duck/api-definitions'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import {
  getStatusBadgeColor,
  formatStatus,
  getFeedbackStatusBadgeColor,
  formatFeedbackStatus,
  formatWorkTime,
  formatFeedbackGrade,
  type Column
} from './utils'
import { RelativeDateTime } from '../datetime/RelativeDateTime'
import { useTar } from '@/modules'
import { Switch } from '@duck/ui'
import { apis } from '../../modules/apiClients'

interface ChallengesTableProps {
  challenges: ChallengeService.ChallengeInfo[]
  totalChallenges: number
  visibleColumns: Column[]
  sortField: keyof ChallengeService.Challenge
  sortDirection: 'asc' | 'desc'
  onSort: (field: keyof ChallengeService.Challenge) => void
  onChallengeClick?: (challengeInfo: ChallengeService.ChallengeInfo) => void
  loadingMode?: boolean
}

export const ChallengesTable: React.FC<ChallengesTableProps> = ({
  challenges,
  totalChallenges,
  visibleColumns,
  sortField,
  sortDirection,
  onSort,
  onChallengeClick,
  loadingMode = false,
}) => {
  const { t } = useTar()

  const [feedbackReleaseState, setFeedbackReleaseState] = useState<Record<string, boolean | undefined>>({})
  const [feedbackReleasePending, setFeedbackReleasePending] = useState<Record<string, true>>({})

  const handleToggleFeedbackRelease = async (challengeInfo: ChallengeService.ChallengeInfo, release: boolean) => {
    const challengeId = challengeInfo.challenge.id
    if (feedbackReleasePending[challengeId]) return

    setFeedbackReleasePending(prev => ({ ...prev, [challengeId]: true }))
    setFeedbackReleaseState(prev => ({ ...prev, [challengeId]: release }))

    try {
      await apis.challengeService.releaseFeedback({ challengeId, release })
    } catch (error) {
      console.error('Failed to update feedback release override', { challengeId, release, error })
      setFeedbackReleaseState(prev => ({ ...prev, [challengeId]: !release }))
    } finally {
      setFeedbackReleasePending(prev => {
        const next = { ...prev }
        delete next[challengeId]
        return next
      })
    }
  }

  const cellRenderers: Partial<Record<keyof ChallengeService.Challenge, (challengeInfo: ChallengeService.ChallengeInfo) => React.ReactNode>> = {
    id: (challengeInfo) => (
      <span className="text-xs font-mono text-muted-foreground">
        {challengeInfo.challenge.id}
      </span>
    ),
    slug: (challengeInfo) => (
      <span className="text-xs font-mono text-muted-foreground">
        {challengeInfo.challenge.slug}
      </span>
    ),
    userId: (challengeInfo) => (
      <span className="text-sm text-foreground">
        {challengeInfo.user?.fullName || challengeInfo.challenge.userId}
      </span>
    ),
    taskId: (challengeInfo) => (
      <span className="text-sm text-foreground">
        {challengeInfo.task?.name || challengeInfo.challenge.taskId}
      </span>
    ),
    status: (challengeInfo) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(challengeInfo.challenge.status)}`}>
        {t(formatStatus(challengeInfo.challenge.status))}
      </span>
    ),
    feedbackStatus: (challengeInfo) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getFeedbackStatusBadgeColor(challengeInfo.challenge.feedbackStatus)}`}>
        {t(formatFeedbackStatus(challengeInfo.challenge.feedbackStatus))}
      </span>
    ),
    workTime: (challengeInfo) => (
      <span className="text-sm text-foreground">
        {formatWorkTime(challengeInfo.challenge.workTime)}
      </span>
    ),
    feedbackGrade: (challengeInfo) => (
      <span className="text-sm text-foreground">
        {formatFeedbackGrade(challengeInfo.challenge.feedbackGrade)}
      </span>
    ),
    startTime: (challengeInfo) => (
      <RelativeDateTime
        isoDateString={challengeInfo.challenge.startTime}
        className="text-sm text-muted-foreground"
      />
    ),
    endTime: (challengeInfo) => (
      challengeInfo.challenge.endTime ? (
        <RelativeDateTime
          isoDateString={challengeInfo.challenge.endTime}
          className="text-sm text-muted-foreground"
        />
      ) : (
        <span className="text-sm text-muted-foreground">
          {t('common.notApplicable')}
        </span>
      )
    ),
    lastWorkTimeUpdate: (challengeInfo) => (
      challengeInfo.challenge.lastWorkTimeUpdate ? (
        <RelativeDateTime
          isoDateString={challengeInfo.challenge.lastWorkTimeUpdate}
          className="text-sm text-muted-foreground"
        />
      ) : (
        <span className="text-sm text-muted-foreground">
          {t('common.notApplicable')}
        </span>
      )
    ),
    workState: (challengeInfo) => {
      if (!challengeInfo.challenge.workState) {
        return (
          <span className="text-sm text-muted-foreground">
            {t('common.notApplicable')}
          </span>
        )
      }

      const { solution, lastQuery } = challengeInfo.challenge.workState
      return (
        <div className="text-sm text-foreground space-y-1">
          {solution && (
            <div className="truncate max-w-xs" title={solution}>
              <span className="text-xs text-muted-foreground">Solution:</span>
              <div className="truncate">{solution}</div>
            </div>
          )}
          {lastQuery && (
            <div className="truncate max-w-xs" title={lastQuery}>
              <span className="text-xs text-muted-foreground">Last Query:</span>
              <div className="truncate font-mono text-xs">{lastQuery}</div>
            </div>
          )}
          {!solution && !lastQuery && (
            <span className="text-sm text-muted-foreground">
              {t('common.notApplicable')}
            </span>
          )}
        </div>
      )
    },
    feedbackText: (challengeInfo) => {
      if (!challengeInfo.challenge.feedbackText) {
        return (
          <span className="text-sm text-muted-foreground">
            {t('common.notApplicable')}
          </span>
        )
      }

      return (
        <div className="text-sm text-foreground max-w-xs">
          <div
            className="truncate"
            title={challengeInfo.challenge.feedbackText}
          >
            {challengeInfo.challenge.feedbackText}
          </div>
        </div>
      )
    },
    userFeedback: (challengeInfo) => {
      const userFeedback = challengeInfo.challenge.userFeedback
      if (!userFeedback) {
        return (
          <span className="text-sm text-muted-foreground">
            {t('common.notApplicable')}
          </span>
        )
      }

      const ratingValue = Number(userFeedback.overallRating)
      const roundedRating = Math.round(ratingValue)
      const hasValidRating = Number.isFinite(ratingValue) && roundedRating >= 1 && roundedRating <= 10

      if (!hasValidRating) {
        return (
          <span className="text-sm text-muted-foreground">
            {t('common.notApplicable')}
          </span>
        )
      }

      return (
        <div
          className="flex items-center gap-1"
          title={userFeedback.freeText?.trim() || undefined}
          aria-label={`User rating: ${roundedRating} out of 6`}
        >
          {Array.from({ length: roundedRating }, (_, index) => (
            <StarIconSolid key={index} className="h-4 w-4 text-warning" aria-hidden="true" />
          ))}
        </div>
      )
    },
    feedbackReleaseOverride: (challengeInfo) => {
      const challengeId = challengeInfo.challenge.id
      const effectiveValue =
        feedbackReleaseState[challengeId] ?? Boolean(challengeInfo.challenge.feedbackReleaseOverride)

      return (
        <div
          className="flex items-center gap-2"
          onClick={(event) => event.stopPropagation()}
        >
          <Switch
            checked={effectiveValue}
            disabled={feedbackReleasePending[challengeId]}
            onCheckedChange={(checked) => handleToggleFeedbackRelease(challengeInfo, Boolean(checked))}
            aria-label={t('challenges.releaseFeedback')}
          />
        </div>
      )
    },
  }

  const renderCellContent = (challengeInfo: ChallengeService.ChallengeInfo, columnKey: keyof ChallengeService.Challenge) => {
    const renderer = cellRenderers[columnKey]
    return renderer ? renderer(challengeInfo) : null
  }

  return (
    <div className="bg-card shadow overflow-hidden rounded-md sm:rounded-md border border-border">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-foreground">
          {t('challenges.allChallenges')}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          {loadingMode ? t('common.loading') : t('challenges.challengesShown', { challengesShown: challenges.length, totalChallenges: totalChallenges })}
        </p>
      </div>

      <div className="overflow-x-auto overflow-y-hidden">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-secondary/5">
            <tr>
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                >
                  {column.sortable ? (
                    <button
                      onClick={() => onSort(column.key as keyof ChallengeService.Challenge)}
                      className="flex items-center space-x-1 hover:text-foreground transition-colors"
                    >
                      <span>{t(column.label)}</span>
                      {sortField === column.key && (
                        <span className="text-accent">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  ) : (
                    t(column.label)
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {loadingMode ? (
              <tr>
                <td colSpan={visibleColumns.length} className="px-6 py-12 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                    <span className="ml-2 text-sm text-muted-foreground">{t('common.loading')}</span>
                  </div>
                </td>
              </tr>
            ) : (
              challenges.map((challengeInfo) => (
                <tr
                  key={challengeInfo.challenge.id}
                  className={`hover:bg-secondary/5 ${onChallengeClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onChallengeClick?.(challengeInfo)}
                >
                  {visibleColumns.map((column) => (
                    <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                      {renderCellContent(challengeInfo, column.key)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!loadingMode && challenges.length === 0 && (
        <div className="text-center py-12">
          <p className="text-sm text-muted-foreground">{t('challenges.noChallenges')}</p>
        </div>
      )}
    </div>
  )
}
